import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { UploadCloud, X, CheckCircle2, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/motion";
import { setPageMeta } from "@/components/seo";
import { BUSINESS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// On the Emergent preview the forms post to the FastAPI backend; on static
// hosting (e.g. GoDaddy cPanel) they post to the same-origin PHP handler.
const shouldUsePhp = () => {
  try {
    return new URL(process.env.REACT_APP_BACKEND_URL).origin !== window.location.origin;
  } catch {
    return true;
  }
};

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  customer_type: "Residential",
  work_type: "",
  project_address: "",
  suburb: "",
  preferred_date: "",
  description: "",
  message: "",
};

const Label = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">
    {children}
  </label>
);

const Quote = () => {
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    setPageMeta(
      "Request a Quote | LAD CO Electrical — Electrician Northern Rivers NSW",
      "Request a quote from LAD CO Electrical — a Level 2 accredited electrician servicing the Northern Rivers NSW. Fast, friendly quotes for residential, commercial and Level 2 electrical work."
    );
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const addPhotos = (files) => {
    const next = [...photos, ...Array.from(files)].slice(0, 5);
    setPhotos(next);
  };

  const removePhoto = (index) => setPhotos(photos.filter((_, i) => i !== index));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const php = shouldUsePhp();
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      photos.forEach((p) => data.append(php ? "photos[]" : "photos", p));
      await axios.post(php ? "/api/quote.php" : `${API}/quote`, data);
      setStatus("sent");
      window.scrollTo(0, 0);
    } catch (err) {
      setStatus("idle");
      setError(err.response?.data?.detail || "Something went wrong submitting your quote request. Please try again or call us directly.");
    }
  };

  if (status === "sent") {
    return (
      <>
        <PageHero eyebrow="Request a Quote" title="Request a Quote" />
        <section className="bg-white">
          <div className="container-site py-20 lg:py-28">
            <div className="max-w-2xl mx-auto border border-volt/40 bg-volt/5 p-10 lg:p-14 text-center" data-testid="quote-success">
              <CheckCircle2 className="w-14 h-14 text-volt mx-auto" />
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-ink mt-6 leading-snug">
                Thanks! Your quote request has been received.
              </h2>
              <p className="text-gray-600 text-base sm:text-lg mt-4">The LAD CO Electrical team will be in touch soon.</p>
              <a href={BUSINESS.phoneHref} className="btn-ghost-dark mt-8" data-testid="quote-success-call">
                <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
              </a>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Request a Quote"
        title="Request a Quote"
        sub="Tell us a little about your electrical requirements and the LAD CO Electrical team will get back to you."
      />

      <section className="bg-[#F4F6F5]" data-testid="quote-section">
        <div className="container-site py-16 lg:py-24">
          <Reveal>
            <form onSubmit={submit} className="bg-white border border-gray-200" data-testid="quote-form">
              <div className="p-8 lg:p-12 border-b border-gray-200">
                <Eyebrow>01 — Contact Details</Eyebrow>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-6">
                  <div>
                    <Label htmlFor="q-name">Full Name *</Label>
                    <input id="q-name" required value={form.full_name} onChange={set("full_name")} className="input-field" data-testid="quote-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="q-email">Email *</Label>
                    <input id="q-email" type="email" required value={form.email} onChange={set("email")} className="input-field" data-testid="quote-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="q-phone">Phone *</Label>
                    <input id="q-phone" type="tel" required value={form.phone} onChange={set("phone")} className="input-field" data-testid="quote-phone-input" />
                  </div>
                </div>
              </div>

              <div className="p-8 lg:p-12 border-b border-gray-200">
                <Eyebrow>02 — Project Details</Eyebrow>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
                  <div>
                    <Label htmlFor="q-type">Customer Type *</Label>
                    <select id="q-type" value={form.customer_type} onChange={set("customer_type")} className="input-field" data-testid="quote-customer-type">
                      <option>Residential</option>
                      <option>Commercial</option>
                      <option>Industrial</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="q-work">Type of Electrical Work *</Label>
                    <input id="q-work" required value={form.work_type} onChange={set("work_type")} className="input-field" placeholder="e.g. Switchboard, lighting, new build" data-testid="quote-work-type-input" />
                  </div>
                  <div>
                    <Label htmlFor="q-address">Project Address</Label>
                    <input id="q-address" value={form.project_address} onChange={set("project_address")} className="input-field" data-testid="quote-address-input" />
                  </div>
                  <div>
                    <Label htmlFor="q-suburb">Suburb</Label>
                    <input id="q-suburb" value={form.suburb} onChange={set("suburb")} className="input-field" data-testid="quote-suburb-input" />
                  </div>
                  <div>
                    <Label htmlFor="q-date">Preferred Date</Label>
                    <input id="q-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className="input-field" data-testid="quote-date-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="q-description">Description of Work *</Label>
                    <textarea id="q-description" required rows={4} value={form.description} onChange={set("description")} className="input-field resize-none" data-testid="quote-description-input" />
                  </div>
                </div>
              </div>

              <div className="p-8 lg:p-12">
                <Eyebrow>03 — Additional Information</Eyebrow>
                <div className="grid grid-cols-1 gap-5 mt-6">
                  <div>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Upload Photos (optional, up to 5)</span>
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="w-full border-2 border-dashed border-gray-300 hover:border-volt transition-colors duration-200 px-6 py-10 flex flex-col items-center gap-3 text-gray-500 hover:text-volt"
                      data-testid="quote-photo-upload-zone"
                    >
                      <UploadCloud className="w-8 h-8" />
                      <span className="text-sm">Click to add photos of the job or site</span>
                    </button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => addPhotos(e.target.files)}
                      data-testid="quote-photo-upload-input"
                    />
                    {photos.length > 0 && (
                      <ul className="mt-4 space-y-2" data-testid="quote-photo-list">
                        {photos.map((p, i) => (
                          <li key={i} className="flex items-center justify-between bg-[#F4F6F5] border border-gray-200 px-4 py-2.5 text-sm text-ink">
                            <span className="truncate mr-4">{p.name}</span>
                            <button type="button" onClick={() => removePhoto(i)} className="text-gray-400 hover:text-volt shrink-0" aria-label={`Remove ${p.name}`} data-testid={`quote-photo-remove-${i}`}>
                              <X className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="q-message">Additional Message</Label>
                    <textarea id="q-message" rows={3} value={form.message} onChange={set("message")} className="input-field resize-none" data-testid="quote-message-input" />
                  </div>
                </div>

                {error && <p className="text-sm text-volt-dark font-semibold mt-6" data-testid="quote-error">{error}</p>}
                <div className="flex flex-col sm:flex-row sm:items-center gap-5 mt-10">
                  <button type="submit" disabled={status === "sending"} className="btn-volt disabled:opacity-60" data-testid="quote-form-submit-button">
                    {status === "sending" ? "Submitting..." : "Request My Quote"}
                  </button>
                  <p className="text-gray-500 text-sm">
                    Prefer to talk? Call <a href={BUSINESS.phoneHref} className="text-volt font-semibold hover:underline" data-testid="quote-call-link">{BUSINESS.phone}</a>
                  </p>
                </div>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Quote;
